import { motion } from 'framer-motion';
import SectionWrapper from '../ui/SectionWrapper';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { projects } from '../../data/projects';
import { FaExternalLinkAlt } from 'react-icons/fa';

const Projects = () => {
    return (
        <SectionWrapper id="projects">
            <motion.h2
                className="text-3xl md:text-4xl font-heading font-bold mb-4 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <span className="text-gradient">Public Projects</span>
            </motion.h2>

            <motion.p
                className="text-text-secondary text-center max-w-2xl mx-auto mb-12 text-sm md:text-base leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
            >
                A selection of work I'm able to showcase publicly — most of my
                projects are protected by NDAs and client confidentiality
                agreements.
            </motion.p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {projects.map((project, index) => (
                    <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                        <Card className="h-full flex flex-col group" hover={true}>
                            {/* Image */}
                            <div className="relative h-48 overflow-hidden">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className={`w-full h-full transition-transform duration-500 group-hover:scale-110 ${
                                        project.imageFit === 'contain' ? 'object-contain p-6 bg-black' : 'object-cover'
                                    }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/90 via-bg-primary/20 to-transparent opacity-60" />

                                {/* Abandoned ribbon */}
                                {project.abandoned && (
                                    <div className="absolute top-3 right-3 z-10">
                                        <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider bg-red-500/90 text-white rounded-md shadow-lg backdrop-blur-sm border border-red-400/30">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            Abandoned
                                        </span>
                                    </div>
                                )}

                                {/* Hover overlay with link */}
                                {project.hasLink && (
                                    <motion.a
                                        href={project.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="absolute inset-0 flex items-center justify-center bg-bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                        whileHover={{ scale: 1 }}
                                    >
                                        <span className="flex items-center gap-2 text-accent-blue font-medium">
                                            Visit Site <FaExternalLinkAlt />
                                        </span>
                                    </motion.a>
                                )}
                            </div>

                            {/* Content */}
                            <div className="p-6 flex-1 flex flex-col">
                                <h3 className="text-xl font-heading font-bold text-text-primary mb-2">
                                    {project.hasLink ? (
                                        <a
                                            href={project.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:text-accent-blue transition-colors"
                                        >
                                            {project.title}
                                        </a>
                                    ) : (
                                        project.title
                                    )}
                                </h3>

                                {/* Tags */}
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {project.tags.map((tag) => (
                                        <span
                                            key={tag}
                                            className="px-3 py-1 text-xs font-medium bg-accent-blue/10 border border-accent-blue/20 rounded-full text-accent-blue"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {/* Description */}
                                <p className="text-text-secondary text-sm leading-relaxed flex-1">
                                    {project.description}
                                </p>

                                {/* Link button */}
                                {project.hasLink && (
                                    <div className="mt-4">
                                        <Button
                                            href={project.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant="secondary"
                                            size="sm"
                                            className="w-full"
                                        >
                                            View Project
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </motion.div>
                ))}
            </div>
        </SectionWrapper>
    );
};

export default Projects;
